import { useCallback, useEffect, useMemo } from 'react'
import { changeTitle, setupWX, TitleSource } from './utils'
import { useLocation } from 'react-use'
import { useHistory, useParams } from 'react-router-dom'
import { Toast } from 'antd-mobile'
import { QueryClient, useIsFetching, useIsMutating } from 'react-query'
import { MutationFilters, QueryFilters } from 'react-query/types/core/utils'
import { analytics, BusinessCard } from './api/backend'
import { useLocalBusinessCard } from './store'
import { useCompanyInfo, useMenu } from './api/query-hooks'

export const useWxSetup = (card: BusinessCard | undefined) => {
  const { href } = useLocation()
  const companyInfo = useCompanyInfo()
  useEffect(() => {
    if (href && card && companyInfo) {
      setupWX(href, card, companyInfo)
    }
  }, [href, card, companyInfo])
}

export const useNavigate = () => {
  const history = useHistory()
  return useCallback((path: string) => history.push(path + history.location.search), [history])
}
export const useCopyToClipboard = () => {
  return useCallback((text: string) => {
    navigator.clipboard.writeText(text).then(() => Toast.show({ content: '已复制' }))
  }, [])
}

// caveat: all toasts, including those unrelated to loading, will be cleared when the loading state changes to false
export const useLoading = (toast: boolean = true, filters?: { query?: QueryFilters, mutation?: MutationFilters }) => {
  const isFetching = useIsFetching(filters?.query) !== 0
  const isMutating = useIsMutating(filters?.mutation) !== 0
  const isLoading = isFetching || isMutating
  useEffect(() => {
    if (toast) {
      if (isLoading) {
        Toast.show({
          icon: 'loading',
          content: '加载中…',
          duration: 0,
        })
      } else {
        Toast.clear()
      }
      return () => Toast.clear()
    }
  }, [isLoading, toast])
  return isLoading
}

export const useSearchParam = (key: string) => {
  const { search } = useLocation()
  const value = useMemo(() => (new URLSearchParams(search)).get(key), [search, key])
  return value ?? undefined
}
export const useCardId = () => {
  const value = useSearchParam('cardId')
  if (value === undefined) {
    if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_CARD_ID !== undefined) {
      return process.env.REACT_APP_CARD_ID
    } else {
      throw new Error('cardId should not be undefined')
    }
  } else {
    return value
  }
}
export const useMenuId = () => {
  const { menuId } = useParams<{ menuId: string | undefined }>()
  if (menuId === undefined) {
    throw new Error('menuId should not be undefined')
  } else {
    const num = parseInt(menuId, 10)
    if (isNaN(num)) {
      throw new Error('menuId should not be NaN')
    }
    return num
  }
}
export const useChangeMenuPageTitle = () => useChangeTitle(useMenu(useMenuId()))

export function usePromise <T>(deps: any[]) {
  return useMemo(() => {
    let resolve: (a: T) => void = () => {}
    let reject: (err: Error) => void = () => {}
    const promise = new Promise<T>((res, rej) => {
      resolve = res
      reject = rej
    })
    return { resolve, promise, reject }
  }, deps)
}

export const useCallNumber = () => useCallback((phoneNumber: string) => window.open(`tel:${phoneNumber}`), [])

const LOADING_TITLE = '加载中...'
export const useTrackPage = () => {
  const { href } = useLocation()
  const cardId = useCardId()
  useEffect(() => {
    const track = () => analytics.page({
      business_card_id: cardId
    })
    let titleMutationObserver: MutationObserver
    if (window.document.title !== LOADING_TITLE) {
      track()
    } else {
      titleMutationObserver = new MutationObserver(([mutation]) => {
        if ((mutation.target as HTMLTitleElement).text !== LOADING_TITLE) {
          track()
        }
      })
      titleMutationObserver.observe(
        document.querySelector('title')!,
        { subtree: true, characterData: true, childList: true }
      )
    }
    return () => titleMutationObserver?.disconnect()
  }, [cardId, href])
}
export const useTrackVisit = () => {
  const cardId = useCardId()
  useEffect(() => {
    analytics.track('VISIT_BUSINESS_CARD', {
      business_card_id: cardId
    })
  }, [cardId])
}
export const useTrackView = () => {
  const cardId = useCardId()
  return useCallback((assetId: number) => {
    if (assetId !== undefined) {
      analytics.track('VIEW_ASSET', {
        asset_id: assetId,
        business_card_id: cardId
      })
    }
  }, [cardId])
}

export const useInitQueryClient = () => {
  return useMemo(() => new QueryClient(), [])
}

export const useNavigateToCard = () => {
  const navigate = useNavigate()
  return useCallback(() => navigate('/'), [navigate])
}

export const useChangeTitle = (obj: TitleSource): void => {
  const card = useLocalBusinessCard()
  useEffect(() => {
    if (card) {
      changeTitle(obj, card.companyInfo.companyName)
    }
  }, [obj, card])
}
